import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

import { writeRefusal } from "./config";
import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { COPY, humanDate, messageFor, MISSING_LABEL, money, pct1, personName, REGIONS } from "./format";
import type { Market, MissingReason } from "./api";
import {
  actionBody, anyFlagged, basisLabel, carriedBackText, isRebuilt, MARGIN_WEEKS_KEY, marginWeeksQuery, MEASURE_LABEL, MEASURES, movePts, moveTone, newestAsOf,
  orderRows, paramsFrom, rebuiltSpan, recordMoveAction, rowKey, rowLabel, WEEK_COUNTS, WEEKLY_COPY, weekDate, weekMonday,
  type ActionWrite, type MarginWeeks, type Measure, type Threshold, type WeekRow,
} from "./margin_weeks";
import { Sparkline } from "./Sparkline";
import { StoreLinesChart } from "./StoreLinesChart";

const missingText = (reasons: string[]) => reasons.map((m) => MISSING_LABEL[m as MissingReason] ?? m).join(", ");

/** A read that failed, with the way to ask again (C1 v1.34): the section's own alert, never a page-wide one, and a Retry any
 * viewer may press (it changes nothing). */
export function RetryAlert({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Alert role="alert" variant="destructive"><AlertDescription>
      {message}<Button size="sm" variant="outline" className="ml-2" data-nonmutating data-retry onClick={onRetry}>{COPY.retry}</Button>
    </AlertDescription></Alert>
  );
}

/** The phrases that name the Config tab in a sentence, longest first: the first one found becomes the link. */
const CONFIG_PHRASES = ["not set in Config", "set in Config", "Config"] as const;
/** A sentence that names Config, with that phrase a link to the Config tab (C1 v1.34). The words are the sentence's own, so the
 * text reads the same with or without the link; a sentence that names no Config is shown as it is. */
export function ConfigText({ text }: { text: string }) {
  const navigate = useNavigate();
  const phrase = CONFIG_PHRASES.find((p) => text.includes(p));
  if (!phrase) return <>{text}</>;
  const at = text.indexOf(phrase);
  const open = () => void navigate({ to: "/app/$area/$module/$tab", params: { area: "margin_engine", module: "precision_pricing", tab: "config" } });
  return (
    <>{text.slice(0, at)}<button type="button" className="text-ks-purple underline underline-offset-2" data-nonmutating data-config-link onClick={open}>{phrase}</button>{text.slice(at + phrase.length)}</>
  );
}

/** The section header the Overview shares across its five sections (numbered, as the approved mockup). */
export function SectionHeading({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span aria-hidden className="inline-flex size-6 items-center justify-center rounded-md bg-ks-purple text-xs font-bold text-white">{n}</span>
      <h2 className="text-lg font-semibold text-ks-ink">{title}</h2>
      <p className="text-sm text-ks-grey-1">{desc}</p>
    </div>
  );
}

/**
 * Section 5 of the Overview (R5, CL-027 first slice): one row per SKU x channel with the API's move for the chosen
 * measure, the store and company lines under it, a row detail sheet. Everything shown is the API's string; the
 * browser orders rows and draws lines, it never subtracts. C1 v1.27: the chip's tone and the action slot follow the
 * API's verdict against the Config threshold (`flagged`), and the slot is the module's one write on this tab.
 */
export function WeeklyMovesSection({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const set = (next: Record<string, unknown>) => void navigate({ to: ".", search: { ...search, ...next }, replace: true });
  const params = paramsFrom(search);
  const query = useQuery(marginWeeksQuery(params));
  const heading = <SectionHeading n={5} title={WEEKLY_COPY.title} desc={WEEKLY_COPY.desc} />;
  if (query.isPending) {
    return <section className="flex flex-col gap-3">{heading}<Card><CardContent className="flex flex-col gap-2 py-4">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-7 w-full" />)}</CardContent></Card></section>;
  }
  if (query.error) {
    return <section className="flex flex-col gap-3">{heading}<RetryAlert message={messageFor(query.error, areaName)} onRetry={() => void query.refetch()} /></section>;
  }
  const data = query.data;
  const open = typeof search.pp_week_row === "string" ? search.pp_week_row : null;
  const rows = orderRows(data.grid.rows, params.measure);
  const openRow = rows.find((r) => rowKey(r) === open) ?? null;

  const toolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label={WEEKLY_COPY.measure} className="flex gap-1">
        {MEASURES.map((key) => key === "true_contribution_pct"
          ? <Button key={key} size="sm" variant="outline" disabled title={WEEKLY_COPY.trueCmOff} aria-pressed={false}>{MEASURE_LABEL[key].short}</Button>
          : <Button key={key} size="sm" variant={key === params.measure ? "default" : "outline"} aria-pressed={key === params.measure} data-nonmutating onClick={() => set({ pp_measure: key === "gross_pct" ? undefined : key })}>{MEASURE_LABEL[key].short}</Button>)}
      </div>
      <div role="group" aria-label={WEEKLY_COPY.region} className="flex gap-1">
        {(["eu", "uk", "ae"] as const).map((key) => (
          <Button key={key} size="sm" variant={key === params.region ? "default" : "outline"} aria-pressed={key === params.region} data-nonmutating onClick={() => set({ pp_region: key, pp_week_row: undefined })}>{REGIONS[key].label}</Button>
        ))}
      </div>
      <label className="flex items-center gap-1 text-sm text-ks-ink">{WEEKLY_COPY.weeks}
        <select aria-label={WEEKLY_COPY.weeks} data-nonmutating className="rounded-md border border-ks-grey-3 bg-white px-2 py-1 text-sm" value={params.weeks} onChange={(e) => set({ pp_weeks: e.target.value === "13" ? undefined : e.target.value })}>
          {WEEK_COUNTS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-1 text-sm text-ks-ink">
        <input type="checkbox" data-nonmutating className="accent-ks-purple" checked={params.grain === "all"} onChange={(e) => set({ pp_grain: e.target.checked ? "all" : undefined })} />{WEEKLY_COPY.showCountries}
      </label>
      {basisLabel(data) && <Badge variant="outline" title={data.grid.label} data-margin-basis={data.grid.margin_basis}>{basisLabel(data)}</Badge>}
      {data.computed_run_id != null && <span className="ml-auto text-xs text-ks-grey-1">{WEEKLY_COPY.calculated(humanDate(newestAsOf(rows)), data.computed_run_id)}</span>}
    </div>
  );

  let body;
  if (data.computed_run_id == null && data.orders.lines.length === 0) body = <Card><CardContent className="py-6 text-ks-ink">{WEEKLY_COPY.empty}</CardContent></Card>;
  else if (rows.length === 0) body = <Card><CardContent className="py-6 text-ks-ink">{WEEKLY_COPY.noRows(REGIONS[params.region].label, params.weeks)}</CardContent></Card>;
  else if (data.weeks.length === 1 || rows.every((r) => r.series.length <= 1)) body = <Card><CardContent className="py-6 text-ks-ink">{WEEKLY_COPY.firstWeek}</CardContent></Card>;
  else body = <Card><CardContent className="p-0">{rows.map((row) => <MoveRow key={rowKey(row)} row={row} data={data} measure={params.measure} areaName={areaName} onOpen={() => set({ pp_week_row: rowKey(row) })} />)}</CardContent></Card>;

  const from = data.weeks.at(-2) ?? data.weeks[0], to = data.weeks.at(-1) ?? data.weeks[0];
  const toDate = to === data.current_week.iso_week;
  const fromText = weekDate(weekMonday(data.current_week, data.weeks, from)), toText = weekDate(weekMonday(data.current_week, data.weeks, to));
  // The count is the rows the slots offer the write on (flagged on any measure, as the API judges the move); the chip alone follows the measure.
  // C1 v1.62: C2's own count of moves still needing an action, once a threshold judges them; an older API serves none.
  const openText = data.open_moves != null && data.threshold.value !== null ? ` · ${WEEKLY_COPY.openMoves(data.open_moves)}` : "";
  const thresholdText = WEEKLY_COPY.threshold(data.threshold, rows.filter(anyFlagged).length) + openText;
  const summary = toDate
    ? WEEKLY_COPY.summaryToDate(MEASURE_LABEL[params.measure].long, fromText, toText, data.current_week.days_elapsed, rows.length, thresholdText)
    : WEEKLY_COPY.summary(MEASURE_LABEL[params.measure].long, fromText, toText, rows.length, thresholdText);

  return (
    <section className="flex flex-col gap-3">
      {heading}
      {/* C1 v1.64 (C2 v1.62): the open issues of the feeds behind these weeks, margin and order feeds alike. */}
      <FeedIssuesNote issues={mergeIssues(data.feed_issues)} />
      <Card>
        <CardContent className="flex flex-col gap-2 py-4">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-ks-ink">{WEEKLY_COPY.linesTitle}</span>
            <span className="text-xs text-ks-grey-1">{WEEKLY_COPY.linesNote}</span>
          </div>
          {data.orders.lines.length === 0 ? <p className="text-sm text-ks-grey-1">{WEEKLY_COPY.noLines}</p> : <StoreLinesChart weeks={data.weeks} lines={data.orders.lines} />}
        </CardContent>
      </Card>
      {toolbar}
      <p className="text-sm text-ks-grey-1"><ConfigText text={summary} /></p>
      {data.owner && <p className="text-sm text-ks-grey-1" data-move-owner><ConfigText text={WEEKLY_COPY.owner(data.owner)} /></p>}
      {body}
      <p className="text-xs text-ks-grey-1">{data.grid.margin_basis != null ? `${data.grid.label} ` : ""}{WEEKLY_COPY.footnote}</p>
      <WeekSheet row={openRow} data={data} onClose={() => set({ pp_week_row: undefined })} />
    </section>
  );
}

const TONE_CLASS = { warn: "border-ks-warn text-ks-warn", pass: "border-ks-pass text-ks-pass" } as const;

function MoveRow({ row, data, measure, areaName, onOpen }: { row: WeekRow; data: MarginWeeks; measure: Measure; areaName: string; onOpen: () => void }) {
  const last = row.series.at(-1);
  const prev = row.move ? row.series.find((p) => p.iso_week === row.move!.from) : null;
  const latest = row.move ? row.series.find((p) => p.iso_week === row.move!.to) : last;
  const move = row.move?.[measure] ?? null;
  const toDate = latest?.iso_week === data.current_week.iso_week;
  let values;
  if (prev && latest && move != null) {
    // The chip's tone is the API's verdict (`flagged`) read with the sign of its own string: neutral within the threshold or without one.
    const tone = moveTone(row, measure);
    values = <><b>{pct1(prev[measure])} → {pct1(latest[measure])}{toDate ? <span className="ml-1 text-xs font-normal text-ks-grey-1">{WEEKLY_COPY.toDate}</span> : null}</b> <Badge variant="outline" className={tone ? TONE_CLASS[tone] : undefined} title={`${row.move!.from} to ${row.move!.to}`}>{movePts(move)}</Badge></>;
  } else if (latest && latest[measure] != null) {
    values = <><b>{pct1(latest[measure])}</b> <Badge variant="outline">{WEEKLY_COPY.oneWeek}</Badge></>;
  } else {
    values = <Badge variant="outline" className="border-ks-warn text-ks-warn">{last && last.missing.length ? missingText(last.missing) : "none"}</Badge>;
  }
  const consecutive = row.move ? data.weeks.indexOf(row.move.to) - data.weeks.indexOf(row.move.from) === 1 : true;
  // C1 v1.31: a move from or to a rebuilt week, or a lone rebuilt week, says so (the sheet names what it carried back).
  const rebuilt = row.move ? (isRebuilt(prev) || isRebuilt(latest) ? WEEKLY_COPY.rebuiltMove : null) : isRebuilt(latest) ? WEEKLY_COPY.rebuiltWeek : null;
  // C1 v1.62: the highlight is C2's `needs_action` true only; false, null (no verdict) and an older API's absent field are plain.
  const open = row.needs_action === true;
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      data-needs-action={open ? "" : undefined}
      className={`flex flex-wrap items-center justify-between gap-3 border-b border-ks-grey-3 px-4 py-2.5 last:border-b-0 hover:bg-ks-purple-tint${open ? " border-l-4 border-l-ks-warn" : ""}`}>
      <div className="text-sm text-ks-ink">{open ? <><Badge variant="outline" className="border-ks-warn text-ks-warn">{WEEKLY_COPY.needsAction}</Badge> </> : null}{rowLabel(row)}: {values}{!consecutive && row.move ? <span className="ml-1 text-xs text-ks-grey-1">{row.move.from.slice(5)} to {row.move.to.slice(5)}</span> : null}{rebuilt ? <span className="ml-1 text-xs text-ks-grey-1" data-rebuilt>{rebuilt}</span> : null}</div>
      <div className="flex items-center gap-4">
        <Sparkline points={row.series} weeks={data.weeks} measure={measure} label={`${rowLabel(row)} ${MEASURE_LABEL[measure].long} over ${data.weeks.length} weeks`} />
        {/* The slot is inside a clickable row: its clicks and keys stay in the slot, so typing a space never opens the sheet. */}
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <MoveActionSlot row={row} market={data.market} measure={measure} threshold={data.threshold} areaName={areaName} />
        </div>
      </div>
    </div>
  );
}

/**
 * The action slot of one row (CL-027 second half): the Config sentence while no threshold is set; nothing on a row with no
 * move; the recorded action whenever the API serves one, with a Change button while the move is still flagged (a raised
 * threshold keeps the record readable, round 1); "within threshold" when the API flagged no measure; else the two
 * write controls ("Record action" opens the editor, "No action needed" writes at once). The write controls and the
 * editor's field stay unmarked (AGENTS rule 11), so a view-only person sees them inert; Cancel carries the marker.
 * C1 v1.62: `needs_action` null (C2 v1.59) reads "no verdict", never "No action needed" or "within threshold".
 * `defaultEditing` lets a test render the editor (a static render cannot press the button).
 */
export function MoveActionSlot({ row, market, measure, threshold, areaName, defaultEditing = false }: {
  row: WeekRow; market: Market; measure: Measure; threshold: Threshold; areaName: string; defaultEditing?: boolean;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(defaultEditing);
  const [text, setText] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const write = useMutation({
    mutationFn: (body: ActionWrite) => recordMoveAction(market, body),
    onMutate: () => setResult(null),   // the alert always belongs to the latest attempt
    onSuccess: () => { setEditing(false); setText(""); void client.invalidateQueries({ queryKey: MARGIN_WEEKS_KEY }); },
    onError: (error) => setResult(writeRefusal(error, areaName)),
  });
  const T = WEEKLY_COPY.action;
  const close = () => { setEditing(false); setText(""); setResult(null); };
  if (threshold.value === null) return <span className="whitespace-nowrap text-xs text-ks-grey-1">{threshold.not_set}</span>;
  // C1 v1.62: C2 gave no verdict on this move (no move, or one it will not judge): say so, and offer neither write.
  if (row.needs_action === null) return <span className="whitespace-nowrap text-xs text-ks-grey-1" data-no-verdict>{WEEKLY_COPY.noVerdict}</span>;
  if (!row.move || !row.flagged) return null;
  const flagged = anyFlagged(row);
  const alert = result ? <p role="alert" className="text-xs text-ks-warn">{result}</p> : null;
  if (editing) {
    const body = text.trim();
    return (
      <form className="flex flex-col gap-1" onSubmit={(e) => { e.preventDefault(); if (body) write.mutate(actionBody(row, measure, "action", body)); }}>
        <Textarea className="min-h-12 w-64 text-xs" maxLength={500} aria-label={T.prompt} placeholder={T.prompt} value={text} disabled={write.isPending} onChange={(e) => setText(e.target.value)} />
        <div className="flex gap-1">
          <Button type="submit" size="sm" disabled={write.isPending || !body}>{write.isPending ? T.saving : T.save}</Button>
          <Button type="button" size="sm" variant="outline" data-nonmutating disabled={write.isPending} onClick={close}>{T.cancel}</Button>
        </div>
        {alert}
      </form>
    );
  }
  if (row.action) {
    const who = personName(row.action.by), when = humanDate(row.action.at);
    return (
      <div className="flex flex-col items-end gap-0.5 text-xs text-ks-ink">
        <span>{row.action.kind === "action" ? T.recorded(row.action.text ?? "", who, when) : T.noneRecorded(who, when)}</span>
        {flagged && <Button type="button" size="sm" variant="link" className="h-auto p-0 text-xs" disabled={write.isPending} onClick={() => setEditing(true)}>{T.change}</Button>}
        {alert}
      </div>
    );
  }
  if (!flagged) return <span className="whitespace-nowrap text-xs text-ks-grey-1">{T.within}</span>;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex gap-1">
        <Button type="button" size="sm" disabled={write.isPending} onClick={() => setEditing(true)}>{T.record}</Button>
        <Button type="button" size="sm" variant="outline" disabled={write.isPending} onClick={() => write.mutate(actionBody(row, measure, "none"))}>{write.isPending ? T.saving : T.none}</Button>
      </div>
      {alert}
    </div>
  );
}

/** The sheet chrome; `WeekDetail` is the body, rendered on its own in tests (a portal does not render server-side). */
export function WeekSheet({ row, data, onClose }: { row: WeekRow | null; data: MarginWeeks; onClose: () => void }) {
  return (
    <Sheet open={row !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      {row && (
        <SheetContent side="right" className="overflow-y-auto data-[side=right]:w-full data-[side=right]:sm:max-w-[max(50vw,480px)]" onCloseAutoFocus={(event) => event.preventDefault()}>
          <SheetHeader>
            <SheetTitle>{rowLabel(row)}</SheetTitle>
            <SheetDescription>{WEEKLY_COPY.sheet.subtitle(row.currency, row.cogs_basis)}</SheetDescription>
          </SheetHeader>
          <WeekDetail row={row} data={data} />
        </SheetContent>
      )}
    </Sheet>
  );
}

export function WeekDetail({ row, data }: { row: WeekRow; data: MarginWeeks }) {
  const navigate = useNavigate();
  const span = rebuiltSpan(row);
  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader><TableRow>{WEEKLY_COPY.sheet.columns.map((c) => <TableHead key={c}>{c}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {row.series.map((p) => (
            <TableRow key={p.iso_week}>
              <TableCell>{p.iso_week}{p.iso_week === data.current_week.iso_week ? ` (${WEEKLY_COPY.toDate})` : ""}</TableCell>
              <TableCell>{humanDate(p.as_of)}{isRebuilt(p) ? <> <Badge variant="outline">{WEEKLY_COPY.sheet.rebuilt}</Badge></> : null}</TableCell>
              <TableCell className="text-right tabular-nums">{money(p.price, row.currency)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(p.net_price, row.currency)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct1(p.gross_pct)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct1(p.cogs_fulfilment_pct)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct1(p.fulfilment_pct)}</TableCell>
              <TableCell className="text-right tabular-nums">{pct1(p.fees_pct)}</TableCell>
              <TableCell>{p.missing.length ? <Badge variant="outline" className="border-ks-warn text-ks-warn">{missingText(p.missing)}</Badge> : null}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {span && <p className="text-sm text-ks-grey-1" data-rebuilt>{WEEKLY_COPY.sheet.rebuiltNote(span.weeks, carriedBackText(span.carried))}</p>}
      <p className="text-sm text-ks-grey-1">{WEEKLY_COPY.sheet.trueCm}</p>
      <p className="text-sm text-ks-grey-1">{WEEKLY_COPY.sheet.source}{" "}
        <Button variant="link" className="h-auto p-0" data-nonmutating onClick={() => void navigate({ to: "/app/$area/$module/$tab", params: { area: "margin_engine", module: "precision_pricing", tab: "price_run" }, search: { pp_region: data.market.toLowerCase() === "ae" ? "ae" : data.market.toLowerCase() } })}>{WEEKLY_COPY.sheet.openPriceRun}</Button>
      </p>
    </div>
  );
}
